import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { trackCta } from "@/lib/cmsApi";

export const trialState = (ent) => {
    if (ent?.has_access && ent?.access_state === "ACTIVE_TRIAL") return "active";
    if (ent?.has_access) return "paid";
    if (ent?.trial_used_at) return "ended";
    return "eligible";
};

export const fmtEnd = (iso) =>
    new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const TrialCta = () => {
    const { user, entitlement, loading, startTrial } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    if (loading) return null;
    if (user && !entitlement) {
        return (
            <div className="text-center" data-testid="trial-checking">
                <button disabled className="btn-primary opacity-60">
                    Checking eligibility…
                </button>
            </div>
        );
    }
    const state = trialState(entitlement);

    const start = async () => {
        if (!user) {
            navigate("/register?trial=1");
            return;
        }
        setBusy(true);
        try {
            await startTrial();
            toast.success("Your 7-day free trial is active — full access, no payment needed.");
            // Attribute the trial start back to the resource that sent the visitor
            const src = sessionStorage.getItem("sx_cta_src");
            if (src) {
                trackCta(src, "trial_start");
                sessionStorage.removeItem("sx_cta_src");
            }
        } catch (err) {
            const code = err?.response?.status;
            if (code === 409) {
                toast.error("Your free trial has already been used. Choose a membership below.");
            } else if (code === 401) {
                toast.error("Please log in to start your free trial.");
                navigate("/login?trial=1");
            } else {
                toast.error("We couldn't start your free trial right now. Please try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    if (state === "active") {
        return (
            <div data-testid="trial-active-panel" className="rounded-2xl border border-[#3fcbf3]/35 bg-[#3fcbf3]/10 p-5 text-center">
                <p className="flex items-center justify-center gap-2 text-sm font-extrabold uppercase tracking-[0.18em] text-[#3fcbf3]">
                    <BadgeCheck className="h-4 w-4" /> 7-Day Free Trial Active
                </p>
                <p className="mt-2 text-sm text-[#c8d3e2]">
                    Ends {fmtEnd(entitlement.trial_ends_at)} · Day {entitlement.trial_day} of{" "}
                    {entitlement.trial_total_days}
                </p>
                <Link to="/account" data-testid="trial-enter-app" className="btn-primary mt-4">
                    Enter SHREDxpro <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    if (state === "ended") {
        return (
            <div data-testid="trial-ended-panel" className="rounded-2xl border border-[#f0356f]/30 bg-[#f0356f]/5 p-5 text-center">
                <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-white">
                    Your free trial has ended
                </p>
                <p className="mt-2 text-sm text-[#9fb0c7]">
                    Continue your SHREDxpro journey by choosing a membership.
                </p>
                <Link to="/#membership" data-testid="trial-choose-membership" className="btn-primary mt-4">
                    Choose Membership <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    if (state === "paid") {
        return (
            <p data-testid="trial-paid-note" className="flex items-center justify-center gap-2 text-sm text-[#9fb0c7]">
                <Info className="h-4 w-4 text-[#3fcbf3]" /> You already have an active SHREDxpro
                membership.
            </p>
        );
    }

    return (
        <div className="text-center" data-testid="trial-eligible">
            <button data-testid="trial-cta-button" onClick={start} disabled={busy} className="btn-primary">
                {busy ? "Activating…" : "Start 7-Day Free Trial"} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#7d8da3]">
                No card · No store purchase · No payment required
            </p>
        </div>
    );
};

export default TrialCta;
